/**
 * Архив досок: /boards/archived.
 *
 * Архивная доска не показывается во вкладках подразделений, поэтому
 * вернуть её можно только отсюда. Список ограничен теми же правилами
 * доступа, что и рабочие доски (§6.5); кнопка «Восстановить» — только
 * с правом board.delete на этой доске.
 */

import { Permission } from "@taskflow/contracts";
import { Undo2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import type { WsError } from "@taskflow/client-core";
import { Button, type Column, DataTable } from "@/shared/ui";
import { ArrowLeft } from "@/shared/ui/motionIcons";

import { type ArchivedBoard, useArchivedBoardsQuery } from "@taskflow/client-core/boards";
import { formatArchivedAt, tasksCount } from "./archiveFormat";
import { RestoreBoardDialog } from "./BoardArchiveDialogs";
import { useDepartmentTabs } from "@taskflow/client-core/boards";

export function ArchivedBoardsPage() {
  const boards = useArchivedBoardsQuery();
  const tabs = useDepartmentTabs();
  const [restoring, setRestoring] = useState<ArchivedBoard | null>(null);

  const columns: Column<ArchivedBoard>[] = [
    {
      key: "name",
      header: "Доска",
      render: (board) => (
        <Link
          to={`/boards/${board.id}/archive`}
          className="font-semibold text-fg hover:text-accent hover:underline"
          title="Открыть архив задач доски"
        >
          {board.name}
        </Link>
      ),
    },
    {
      key: "department",
      header: "Подразделение",
      width: "130px",
      render: (board) => (
        <span className="text-[13px] text-muted">
          {tabs.labels[board.departmentCode] ?? board.departmentCode}
        </span>
      ),
    },
    {
      key: "project",
      header: "Проект",
      width: "170px",
      render: (board) => (
        <span className="text-[13px] text-muted">
          {board.projectShortName ?? board.projectName ?? "Проекты по столбцам"}
        </span>
      ),
    },
    {
      key: "tasks",
      header: "Задачи с доской",
      width: "130px",
      render: (board) => (
        <span className="text-[13px] text-muted tabular-nums">
          {board.archivedTaskCount === 0 ? "—" : tasksCount(board.archivedTaskCount)}
        </span>
      ),
    },
    {
      key: "archivedAt",
      header: "Дата архивации",
      width: "150px",
      render: (board) => (
        <span className="text-[13px] text-fg tabular-nums">{formatArchivedAt(board.archivedAt)}</span>
      ),
    },
    {
      key: "archivedBy",
      header: "Архивировал",
      width: "160px",
      render: (board) => (
        <span className="text-[13px] text-muted">{board.archivedByName ?? "Система"}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "150px",
      align: "right",
      render: (board) =>
        board.permissions.includes(Permission.BOARD_DELETE) ? (
          <Button
            size="sm"
            variant="secondary"
            icon={Undo2}
            onClick={(event) => {
              event.stopPropagation();
              setRestoring(board);
            }}
          >
            Восстановить
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col">
      <header className="flex shrink-0 items-center justify-between gap-4 border-b border-line bg-surface px-4 py-2.5">
        <div className="min-w-0">
          <h1 className="truncate text-[15px] font-extrabold text-fg">Архив досок</h1>
          <p className="text-[11px] text-faint">
            Доски сохраняют задачи, участников и историю
          </p>
        </div>
        <Link to="/tasks">
          <Button variant="secondary" size="sm" icon={ArrowLeft}>
            К доскам
          </Button>
        </Link>
      </header>

      <DataTable
        caption="Архивные доски"
        columns={columns}
        rows={boards.data?.data ?? []}
        rowKey={(board) => board.id}
        loading={boards.isLoading}
        error={
          boards.error === undefined
            ? null
            : {
                message: (boards.error as WsError).data.message,
                correlationId: (boards.error as WsError).data.correlationId,
              }
        }
        onRetry={() => void boards.refetch()}
        empty={{
          title: "В архиве нет досок",
          description: "Доски, перенесённые в архив, будут отображаться здесь.",
        }}
      />

      {restoring !== null && (
        <RestoreBoardDialog board={restoring} onClose={() => setRestoring(null)} />
      )}
    </div>
  );
}
